"use client";

import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import { gothamFonts } from "@lattice/frontend/lib/utils/ui";

type T_TradeSummaryChartProps = {
  data: ({
    transactionDate: Date;
    isBuy: boolean;
    isSell: boolean;
    amount: number;
  } & { id: string })[];
};

type T_AggregatedData = {
  period: string;
  buyVolume: number;
  sellVolume: number;
};

function determineAggregationLevel(data: T_TradeSummaryChartProps["data"]) {
  if (data.length === 0) return "day";

  const dates = data.map((d) => new Date(d.transactionDate));
  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

  const daysDiff =
    (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24);

  if (daysDiff <= 14) return "day"; // Up to 2 weeks: show daily
  if (daysDiff <= 90) return "week"; // Up to 3 months: show weekly
  if (daysDiff <= 365) return "month"; // Up to 1 year: show monthly
  return "year"; // More than 1 year: show yearly
}

function getWeekStartDate(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  return new Date(d.setDate(diff));
}

function formatPeriod(date: Date, level: string): string {
  switch (level) {
    case "day":
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    case "week":
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    case "month":
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      });
    case "year":
      return date.getFullYear().toString();
    default:
      return date.toLocaleDateString();
  }
}

function aggregateData(
  data: T_TradeSummaryChartProps["data"],
  level: string,
): T_AggregatedData[] {
  const aggregated = new Map<string, { buy: number; sell: number }>();

  data.forEach((trade) => {
    const date = new Date(trade.transactionDate);
    let key: string;

    switch (level) {
      case "day":
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        break;
      case "week": {
        const weekStart = getWeekStartDate(date);
        key = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
        break;
      }
      case "month":
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        break;
      case "year":
        key = date.getFullYear().toString();
        break;
      default:
        key = date.toISOString().split("T")[0] ?? "";
    }

    const current = aggregated.get(key) || { buy: 0, sell: 0 };

    if (trade.isBuy) {
      current.buy += trade.amount;
    } else if (trade.isSell) {
      current.sell += trade.amount;
    }

    aggregated.set(key, current);
  });

  // Convert to array and sort by date
  const result: T_AggregatedData[] = [];

  Array.from(aggregated.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([key, values]) => {
      let displayDate: string;

      switch (level) {
        case "day": {
          const parts = key.split("-");
          const date = new Date(
            parseInt(parts[0] ?? "0"),
            parseInt(parts[1] ?? "1") - 1,
            parseInt(parts[2] ?? "1"),
          );
          displayDate = formatPeriod(date, level);
          break;
        }
        case "week": {
          const parts = key.split("-");
          const date = new Date(
            parseInt(parts[0] ?? "0"),
            parseInt(parts[1] ?? "1") - 1,
            parseInt(parts[2] ?? "1"),
          );
          displayDate = formatPeriod(date, level);
          break;
        }
        case "month": {
          const parts = key.split("-");
          const date = new Date(
            parseInt(parts[0] ?? "0"),
            parseInt(parts[1] ?? "1") - 1,
            1,
          );
          displayDate = formatPeriod(date, level);
          break;
        }
        case "year":
          displayDate = key;
          break;
        default:
          displayDate = key;
      }

      // Only create entry if there are actual transactions
      if (values.buy > 0 || values.sell > 0) {
        result.push({
          period: displayDate,
          buyVolume: values.buy || 0,
          sellVolume: values.sell > 0 ? -values.sell : 0, // Negative for display, 0 if no sells
        });
      }
    });

  return result;
}

export default function TradeSummaryChart({ data }: T_TradeSummaryChartProps) {
  const aggregationLevel = React.useMemo(
    () => determineAggregationLevel(data),
    [data],
  );

  const chartData = React.useMemo(
    () => aggregateData(data, aggregationLevel),
    [data, aggregationLevel],
  );

  // Calculate net values for the second chart
  const netChartData = React.useMemo(
    () =>
      chartData.map((d) => ({
        period: d.period,
        netVolume: d.buyVolume + d.sellVolume, // Net value (buys - sells, since sells are negative)
      })),
    [chartData],
  );

  // Calculate shared domain for both charts to ensure same y-axis
  const sharedDomain = React.useMemo(() => {
    if (chartData.length === 0) return { min: -1000, max: 1000 };

    // Get all values from both charts
    const allValues = [
      ...chartData.flatMap((d) => [d.buyVolume, d.sellVolume]),
      ...netChartData.map((d) => d.netVolume),
    ];

    const minVal = Math.min(0, ...allValues);
    const maxVal = Math.max(0, ...allValues);

    // Add 5% padding to prevent bars from touching edges
    const padding = Math.max(Math.abs(minVal), Math.abs(maxVal)) * 0.05;
    return {
      min: Math.floor(minVal - padding),
      max: Math.ceil(maxVal + padding),
    };
  }, [chartData, netChartData]);

  const formatValue = (value: number) => {
    const absValue = Math.abs(Number(value));
    if (absValue >= 1_000_000) {
      return `${Math.round(absValue / 1_000_000)}M`;
    }
    if (absValue >= 1_000) {
      return `${Math.round(absValue / 1_000)}K`;
    }
    return absValue.toString();
  };

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-2 bg-grey-7 rounded-lg p-4 sm:p-6 border border-[#131314]">
      <div>
        <div className="mb-4 text-center h-8 flex items-center justify-center">
          <p
            className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
            style={gothamFonts.sidebarSubheader}
          >
            BUY Vs SELL VOLUME
          </p>
        </div>
        <div className="h-[30vh] md:[h-600px]">
          <ResponsiveBar
            data={chartData}
            keys={["buyVolume", "sellVolume"]}
            indexBy="period"
            layout="vertical"
            margin={{ top: 10, right: 30, bottom: 80, left: 60 }}
            padding={0.35}
            groupMode="stacked"
            animate={false}
            // Force identical y-scale for perfect alignment
            valueScale={{
              type: "linear",
              min: sharedDomain.min,
              max: sharedDomain.max,
            }}
            indexScale={{ type: "band", round: true }}
            enableGridY={true}
            enableGridX={false}
            markers={[
              {
                axis: "y",
                value: 0,
                lineStyle: { stroke: "#666", strokeWidth: 2 },
              },
            ]}
            axisBottom={{
              tickRotation: 0,
              tickPadding: 5,
              tickSize: 5,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              format: formatValue,
            }}
            labelSkipWidth={999} // Hide in-bar labels
            labelSkipHeight={999}
            // Color function for buy (green) and sell (red)
            colors={(bar) => (bar.id === "buyVolume" ? "#10b981" : "#ef4444")}
            borderRadius={2}
            theme={{
              grid: {
                line: {
                  stroke: "#374151",
                  strokeDasharray: "2,4",
                },
              },
              axis: {
                ticks: {
                  text: {
                    fill: "#9ca3af",
                    fontSize: 10,
                  },
                  line: {
                    stroke: "#4b5563",
                  },
                },
                domain: {
                  line: {
                    stroke: "#4b5563",
                  },
                },
              },
            }}
            tooltip={(props) => {
              const id = String(props.id);
              const value = Number(props.value);
              const indexValue = String(props.indexValue);
              return (
                <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg text-sm">
                  <div className="font-semibold mb-1">{indexValue}</div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{
                        backgroundColor:
                          id === "buyVolume" ? "#10b981" : "#ef4444",
                      }}
                    />
                    <span>
                      {id === "buyVolume" ? "Buy: " : "Sell: "}
                      {Intl.NumberFormat().format(Math.abs(Number(value)))}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      <div>
        <div className="mb-4 text-center h-8 flex items-center justify-center">
          <p
            className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
            style={gothamFonts.sidebarSubheader}
          >
            NET VOLUME
          </p>
        </div>
        <div className="h-[30vh] md:[h-600px]">
          <ResponsiveBar
            data={netChartData}
            keys={["netVolume"]}
            indexBy="period"
            layout="vertical"
            margin={{ top: 10, right: 30, bottom: 80, left: 60 }}
            padding={0.35}
            animate={false}
            groupMode="stacked"
            // Force identical y-scale for perfect alignment
            valueScale={{
              type: "linear",
              min: sharedDomain.min,
              max: sharedDomain.max,
            }}
            indexScale={{ type: "band", round: true }}
            enableGridY={true}
            enableGridX={false}
            markers={[
              {
                axis: "y",
                value: 0,
                lineStyle: { stroke: "#666", strokeWidth: 2 },
              },
            ]}
            axisBottom={{
              tickRotation: 0,
              tickPadding: 5,
              tickSize: 5,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              format: formatValue,
            }}
            labelSkipWidth={999} // Hide in-bar labels
            labelSkipHeight={999}
            // Green for positive net values, red for negative
            colors={(bar) => (bar.data.netVolume >= 0 ? "#10b981" : "#ef4444")}
            borderRadius={2}
            theme={{
              grid: {
                line: {
                  stroke: "#374151",
                  strokeDasharray: "2,4",
                },
              },
              axis: {
                ticks: {
                  text: {
                    fill: "#9ca3af",
                    fontSize: 10,
                  },
                  line: {
                    stroke: "#4b5563",
                  },
                },
                domain: {
                  line: {
                    stroke: "#4b5563",
                  },
                },
              },
            }}
            tooltip={(props) => {
              const value = Number(props.value);
              const indexValue = String(props.indexValue);
              return (
                <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg text-sm">
                  <div className="font-semibold mb-1">{indexValue}</div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{
                        backgroundColor:
                          Number(value) >= 0 ? "#10b981" : "#ef4444",
                      }}
                    />
                    <span>
                      {Number(value) >= 0 ? "Net Buy: " : "Net Sell: "}
                      {Intl.NumberFormat().format(Math.abs(Number(value)))}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
